import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { usePlaid } from "hooks/usePlaid";
import { useMutation } from "hooks/useMutation";
import { createACHLink } from "features/SurmountAccount/service";
import { toast } from "Layout/slice";
import { parseErrorMessage } from "utils";
import PlaidConnectionModal from "shared/Modal/PlaidConnectionModal";

const PlaidBankConnector = ({ children, onSuccess, onPlaidSuccess }) => {
  const dispatch = useDispatch();
  const [showPlaidModal, setShowPlaidModal] = useState(false);
  const [plaidActive, setPlaidActive] = useState(false);

  // Create ACH Link Mutation
  const createACHMutation = useMutation({
    mutationFn: ({ public_token, accountId }) =>
      createACHLink({ public_token, accountId }),
    onSuccess: (data) => {
      dispatch(
        toast({
          type: "success",
          body: "Bank account connected successfully!",
        })
      );
      setPlaidActive(false);
      if (onSuccess) onSuccess(data);
    },
    onError: (error) => {
      dispatch(
        toast({
          type: "error",
          body:
            parseErrorMessage(error) ||
            "Failed to connect bank account. Please try again.",
        })
      );
      setPlaidActive(false);
    },
  });

  const handlePlaidSuccess = (public_token, metadata) => {
    if (onPlaidSuccess) onPlaidSuccess(public_token, metadata);
    createACHMutation.mutate({
      public_token,
      accountId: metadata.account_id,
    });
  };

  const handlePlaidExit = () => {
    setPlaidActive(false);
  };

  const { open, ready } = usePlaid({
    active: plaidActive,
    successCallback: handlePlaidSuccess,
    exitCallback: handlePlaidExit,
  });

  useEffect(() => {
    if (ready && plaidActive) {
      open();
    }
  }, [ready, plaidActive, open]);

  const openConnector = () => {
    setShowPlaidModal(true);
    createACHMutation.reset();
  };

  const handlePlaidModalClose = () => {
    setShowPlaidModal(false);
    setPlaidActive(false);
  };

  const handlePlaidConnect = () => {
    setShowPlaidModal(false);
    setPlaidActive(true);
  };

  return (
    <>
      {children({
        openConnector,
        isLoading: createACHMutation.isLoading || plaidActive,
      })}

      <PlaidConnectionModal
        show={showPlaidModal}
        onHide={handlePlaidModalClose}
        onConnect={handlePlaidConnect}
        isLoading={createACHMutation.isLoading}
      />
    </>
  );
};

PlaidBankConnector.propTypes = {
  children: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  onPlaidSuccess: PropTypes.func,
};

export default PlaidBankConnector;
